import React, { useEffect, useState } from 'react';
import { Plus, FolderOpen, Trash2, X, Loader2, RefreshCw, Edit2 } from 'lucide-react';
import { crmCategoriesApi, CrmCategory } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Categories() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [categories, setCategories] = useState<CrmCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CrmCategory | null>(null);
  const [formData, setFormData] = useState({
    Name: '',
    Description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await crmCategoriesApi.list();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, []);
  // Filter by team and region scope
  const filteredCategories = categories.filter(c => {
    // Team filter: only show categories belonging to current workspace or with no team
    if (c.Team && c.Team !== currentWorkspace.name && c.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !c.Region || c.Region === currentSubRegion;
    }
    return true;
  });
  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({
      Name: '',
      Description: ''
    });
    setShowModal(true);
  };
  const handleOpenEdit = (cat: CrmCategory) => {
    setEditingCategory(cat);
    setFormData({
      Name: cat.Name || '',
      Description: cat.Description || ''
    });
    setShowModal(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.Name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: {
        Name: string;
        Description?: string;
        Team: string;
        Region?: string;
      } = {
        Name: formData.Name.trim(),
        Description: formData.Description.trim() || undefined,
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      if (editingCategory) {
        await crmCategoriesApi.update(editingCategory.id, payload);
        toast.success('Category updated successfully');
      } else {
        await crmCategoriesApi.create(payload);
        toast.success('Category created successfully');
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      console.error('Failed to save category:', err);
      toast.error(editingCategory ? 'Failed to update category' : 'Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await crmCategoriesApi.delete(id);
      toast.success('Category deleted successfully');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err) {
      console.error('Failed to delete category:', err);
      toast.error('Failed to delete category');
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-3325" data-mp="1luw46ucvoqub">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3326" data-mp="1lvo1qaq1cyn8">
        <div className="min-w-0" data-id="element-3327" data-mp="1qth8b4v93uo1">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3328" data-mp="agscxvxodgsy">Categories</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3329" data-mp="aivb538zeg8r">
            Manage contact categories (synced with Zoho CRM).
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3330" data-mp="uq1zotsjlc5p">
          <button onClick={fetchCategories} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-3331" data-mp="uqtkraoijrhq">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3332" data-mp="gywu7b2htpng" />
          </button>
          <button onClick={handleOpenCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-3333" data-mp="1dommwarbbxe2">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-3334" data-mp="14lv7y0z8ajho" />
            <span data-id="element-3335" data-mp="zp4uzmiq040h">Create Category</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3336" data-mp="3pdnldmf5h6qp">
        {loading && filteredCategories.length === 0 ? <div className="p-12 text-center" data-id="element-3337" data-mp="3ke19bgb25wjh">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3338" data-mp="1zws1ltahqv01" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3339" data-mp="1zup12fbppjh2">
              Loading categories from CRM...
            </p>
          </div> : filteredCategories.length === 0 ? <div className="p-12 text-center" data-id="element-3340" data-mp="28vwyywabgiam">
            <FolderOpen className="mx-auto h-12 w-12 text-gray-400" data-id="element-3341" data-mp="2do6mk5nyl69q" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3342" data-mp="2dqaxv8o6t7k1">
              No categories found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3343" data-mp="2nmdz8cb7t25x">
              {currentSubRegion ? `No categories found for region "${currentSubRegion}".` : 'Create categories to organize your contacts.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-3344" data-mp="2wm9dwr3zbfap">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3345" data-mp="y7y0i56kmtk5">
              <thead className="bg-gray-50" data-id="element-3346" data-mp="y9sm7lpo2ppa">
                <tr data-id="element-3347" data-mp="1289lxk6rcxwo">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3348" data-mp="129jkeahiw83u">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3349" data-mp="20ldp2krpjvpi">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3350" data-mp="2eiagoofnhh7g">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3351" data-mp="2ohi27ywjgi5n">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3352" data-mp="2y94c9rx54b6t">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3353" data-mp="2tbm96i5t0q6q">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3354" data-mp="328d58ukcw51b">
                {filteredCategories.map(cat => <tr key={cat.id} className="hover:bg-gray-50" data-id="element-3355" data-mp="3quuf31jh6d5z">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3356" data-mp="3qszz81n29b8r">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200" data-id="element-3357" data-mp="3cujidhvhi4cw">
                        <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" data-id="element-3358" data-mp="334zz4b3v08hi" />
                        {cat.Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" data-id="element-3359" data-mp="37y1iavb3mu6w">
                      {cat.Description || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3360" data-mp="2y76v9oq13qke">
                      {cat.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3361" data-mp="2p5qv09sxh4tw">
                      {cat.Region || 'All Regions'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3362" data-mp="34n8fw0z6v817">
                      {cat.Created_Time ? new Date(cat.Created_Time).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3363" data-mp="2qoiggeowcw9o">
                      <div className="flex items-center justify-end gap-2" data-id="element-3364" data-mp="2lpe97tlj98lz">
                        <button onClick={() => handleOpenEdit(cat)} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-3365" data-mp="2gy5x2pstj954">
                          <Edit2 className="h-4 w-4" data-id="element-3366" data-mp="271ktm8wg663e" />
                        </button>
                        <button onClick={() => setDeleteConfirm(cat.id)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-3367" data-mp="22x39u9q37ssm">
                          <Trash2 className="h-4 w-4" data-id="element-3368" data-mp="ig3x6opf9olq" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create/Edit Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3369" data-mp="3qwymt2h7lchv">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3370" data-mp="3qucev1qeouz4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-3371" data-mp="3uy0pjtac46ol" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3372" data-mp="8ipppelh8a5k">
              <div className="flex items-center justify-between mb-4" data-id="element-3373" data-mp="378g3h1ivwtb3">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3374" data-mp="3763nuv0lmv17">
                  {editingCategory ? 'Edit Category' : 'Create New Category'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-3375" data-mp="3pzudoutvi4j">
                  <X className="h-5 w-5" data-id="element-3376" data-mp="crpncz4cyp4f" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3377" data-mp="hqj0gqfqge42">
                <div data-id="element-3378" data-mp="hprgkcvi04tm">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3379" data-mp="ho6k2hpmibt4">
                    Category Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Name} onChange={e => setFormData({
                ...formData,
                Name: e.target.value
              })} placeholder="e.g., Influencer, Partner, Sponsor" data-id="element-3380" data-mp="3l6hnqv2tgnww" />
                </div>
                <div data-id="element-3381" data-mp="2pyfif5douhd8">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3382" data-mp="2pxw747ijmr86">
                    Description (Optional)
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Description} onChange={e => setFormData({
                ...formData,
                Description: e.target.value
              })} placeholder="What is this category used for?" data-id="element-3383" data-mp="2l0w3e39620k8" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-3384" data-mp="1s941ubxogozn">
                  <div data-id="element-3385" data-mp="1o92aavlnpby3">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3386" data-mp="1o8k5vboubrf3">
                      Team
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-3387" data-mp="1dmdqehq8pg0c" />
                  </div>
                  <div data-id="element-3388" data-mp="19o7phqc31unc">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3389" data-mp="1xbxy1oulci6s">
                      Region
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentSubRegion || 'All Regions'} disabled data-id="element-3390" data-mp="21bzr59m9g3ag" />
                  </div>
                </div>
                <p className="text-xs text-gray-400" data-id="element-3391" data-mp="2l1s8jzrftunv">
                  Team and Region are set from your current scope
                </p>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3392" data-mp="2ueixkm7xrpe6">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3393" data-mp="2qf0fdy7i1sf1">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formData.Name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3394" data-mp="2bhzizhed59a8">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3395" data-mp="p6i3pmbv1th" />}
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3396" data-mp="26e8hp78dk9fq">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3397" data-mp="2bcs5ld6j3vaq">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-3398" data-mp="2bewjmny56aoq" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-3399" data-mp="2kc6w2cmp45v8">
              <div className="flex items-center gap-4 mb-4" data-id="element-3400" data-mp="2pbjf0mag4ssj">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3401" data-mp="2p9yod7r95tzy">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-3402" data-mp="2z1uvf8bbgugb" />
                </div>
                <div data-id="element-3403" data-mp="2z3xndxw14xno">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3404" data-mp="33480rvm5iqck">
                    Delete Category
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3405" data-mp="383knmgd0vxf5">
                    Are you sure? This will remove the category from CRM.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-3406" data-mp="rfb8qwroyx92">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3407" data-mp="nerdjyqoi3pk">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3408" data-mp="1b3tut5hehujj">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3409" data-mp="1yuzkecd58171" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}